import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight, ArrowUpRight, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const servicesRef = useRef(null);
  const moreRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsServicesOpen(false);
    setIsMoreOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setIsServicesOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setIsMoreOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainLinks = [
    { name: 'Products', path: '/products' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' },
  ];

  const serviceLinks = [
    { name: 'All Services', path: '/services' },
    { name: 'Custom Trophies', path: '/services/trophies', desc: 'Sports & corporate' },
    { name: 'Plaques', path: '/services/plaques', desc: 'Wood, acrylic & metal' },
    { name: 'Engraving', path: '/services/engraving', desc: 'Same-day available' },
    { name: 'Embroidery', path: '/services/embroidery', desc: 'Logos & uniforms' },
    { name: 'Screen Printing', path: '/services/screen-printing', desc: 'T-shirts & apparel' },
    { name: 'Promotional Products', path: '/services/promotional-products', desc: 'Branded merch' },
    { name: 'Award Design', path: '/services/design', desc: 'Custom creation' },
  ];

  const moreLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Location', path: '/location' },
  ];

  const allMobileLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    ...serviceLinks,
    { name: 'Gallery', path: '/gallery' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' },
    ...moreLinks,
  ];

  return (
    <nav data-testid="main-navbar" className="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
      <div
        className={`max-w-7xl mx-auto transition-all duration-500 rounded-2xl px-4 lg:px-6 py-3 flex items-center justify-between ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg border border-gray-100'
            : 'bg-white/90 backdrop-blur-sm shadow-md border border-gray-100/50'
        }`}
      >
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 flex-shrink-0 bg-[#1a365d] rounded-xl px-3 py-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_hospitality-hub-303/artifacts/55gdqw83_RC%20awards%20new%20logo%20-2.png" 
            alt="RC Awards & Promotions" 
            className="h-10 w-auto object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center gap-2">
            <span className="text-lg font-bold text-[#D4AF37]">RC Awards</span>
          </div>
        </Link>

        {/* Desktop Links */}
        <div className="hidden lg:flex items-center gap-1">
          <Link
            to="/products"
            data-testid="nav-link-products"
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
              location.pathname === '/products'
                ? 'text-[#1a365d] bg-[#1a365d]/5'
                : 'text-gray-600 hover:text-[#1a365d] hover:bg-[#1a365d]/5'
            }`}
          >
            Products
          </Link>

          {/* Services Dropdown */}
          <div ref={servicesRef} className="relative">
            <button
              onClick={() => {
                setIsServicesOpen(!isServicesOpen);
                setIsMoreOpen(false);
              }}
              data-testid="nav-services-dropdown"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                isServicesOpen || location.pathname.startsWith('/services')
                  ? 'text-[#1a365d] bg-[#1a365d]/5'
                  : 'text-gray-600 hover:text-[#1a365d] hover:bg-[#1a365d]/5'
              }`}
            >
              Services
              <ChevronDown size={14} className={`transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {isServicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden py-2"
                >
                  {serviceLinks.map((link, index) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`nav-service-${link.name.toLowerCase().replace(' ', '-')}`}
                      className={`block px-4 py-3 transition-colors ${
                        location.pathname === link.path
                          ? 'bg-[#1a365d]/5 text-[#1a365d]'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-[#1a365d]'
                      } ${index === 0 ? 'border-b border-gray-100 mb-1' : ''}`}
                    >
                      <span className={`font-medium ${index === 0 ? 'text-[#D4AF37]' : ''}`}>{link.name}</span>
                      {link.desc && <span className="block text-xs text-gray-500 mt-0.5">{link.desc}</span>}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {mainLinks.filter(l => l.name !== 'Products').map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.name.toLowerCase()}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                location.pathname === link.path
                  ? 'text-[#1a365d] bg-[#1a365d]/5'
                  : 'text-gray-600 hover:text-[#1a365d] hover:bg-[#1a365d]/5'
              }`}
            >
              {link.name}
            </Link>
          ))}

          {/* More Dropdown */}
          <div ref={moreRef} className="relative">
            <button
              onClick={() => {
                setIsMoreOpen(!isMoreOpen);
                setIsServicesOpen(false);
              }}
              data-testid="nav-more-dropdown"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                isMoreOpen || moreLinks.some((l) => location.pathname === l.path)
                  ? 'text-[#1a365d] bg-[#1a365d]/5'
                  : 'text-gray-600 hover:text-[#1a365d] hover:bg-[#1a365d]/5'
              }`}
            >
              More
              <ChevronDown size={14} className={`transition-transform ${isMoreOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {isMoreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden py-1"
                >
                  {moreLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`nav-more-${link.name.toLowerCase().replace(' ', '-')}`}
                      className={`block px-4 py-2.5 text-sm transition-colors ${
                        location.pathname === link.path
                          ? 'bg-[#1a365d]/5 text-[#1a365d] font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-[#1a365d]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right Actions */}
        <div className="hidden lg:flex items-center gap-2">
          {/* Phone */}
          <a
            href="tel:6196401633"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-[#1a365d] hover:bg-[#1a365d]/5 transition-all duration-300"
          >
            <Phone size={14} />
            <span className="hidden xl:inline">(619) 640-1633</span>
          </a>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-200 mx-1"></div>

          {/* Shop CTA */}
          <a
            href="https://www.rcawards.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-shop-btn"
            className="bg-[#1a365d] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#152a4a] transition-all duration-300 flex items-center gap-1.5"
          >
            Shop Online
            <ArrowUpRight size={14} />
          </a>

          {/* Quote CTA */}
          <Link
            to="/contact"
            data-testid="navbar-quote-btn"
            className="bg-[#D4AF37] text-[#1a365d] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#c9a432] transition-all duration-300 flex items-center gap-1.5"
          >
            Get Quote
            <ArrowRight size={14} />
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-3">
          <a
            href="tel:6196401633"
            className="flex items-center justify-center w-9 h-9 rounded-lg text-[#1a365d] bg-[#1a365d]/5"
          >
            <Phone size={18} />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-center w-9 h-9 rounded-lg text-[#1a365d] bg-[#1a365d]/5"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            data-testid="mobile-menu"
            className="lg:hidden mt-2 mx-auto max-w-7xl bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
          >
            <div className="p-4 space-y-1 max-h-[70vh] overflow-y-auto">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-3 px-4 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  location.pathname === '/'
                    ? 'bg-[#1a365d] text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Home
              </Link>
              
              <Link
                to="/products"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-3 px-4 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  location.pathname === '/products'
                    ? 'bg-[#1a365d] text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Products
              </Link>

              {/* Services Section in Mobile */}
              <div className="pt-2 pb-1">
                <p className="px-4 text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Services</p>
              </div>
              {serviceLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 px-4 rounded-xl text-sm font-medium transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'bg-[#1a365d] text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                  {link.desc && <span className="block text-xs text-gray-400 font-normal">{link.desc}</span>}
                </Link>
              ))}

              <div className="pt-2 pb-1">
                <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">More</p>
              </div>
              
              {[...mainLinks.filter(l => l.name !== 'Products'), ...moreLinks].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 px-4 rounded-xl text-sm font-medium transition-colors duration-200 ${
                    location.pathname === link.path
                      ? 'bg-[#1a365d] text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              <div className="pt-4 mt-3 border-t border-gray-100 space-y-2">
                <a
                  href="https://www.rcawards.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block bg-[#1a365d] text-white py-3 rounded-xl text-sm font-medium text-center hover:bg-[#152a4a] transition-colors"
                >
                  Shop Online
                </a>
                <Link
                  to="/contact"
                  data-testid="mobile-quote-btn"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block bg-[#D4AF37] text-[#1a365d] py-3 rounded-xl text-sm font-semibold text-center hover:bg-[#c9a432] transition-colors"
                >
                  Get a Free Quote
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
